import { useState, useEffect, useCallback, useRef } from 'react';
import { makeApiCall } from '../utils/api';
import { API_PATHS } from '../config/api-endpoints';
import { transformSchedule, transformSchedules } from '../utils/schedule-transformer';
import {
  Schedule,
  ScheduleStatus,
  ScheduleFrequencyType,
  ListSchedulesRequest,
  ListSchedulesResponse,
  CreateScheduleRequest,
  UpdateScheduleRequest,
  UpdateScheduleStatusRequest,
} from '../types';

/**
 * Form data for creating or updating a schedule
 * Uses UI-friendly field names that get transformed to backend format
 */
export interface ScheduleFormData {
  scheduleName: string;
  description?: string;
  scheduleExpression: string; // Full expression: cron(...) or rate(...)
  frequencyType: ScheduleFrequencyType;
  timezone?: string;
}

/**
 * Options for the useSchedules hook
 */
export interface UseSchedulesOptions {
  jobId: string;
  enabled?: boolean;
}

/**
 * Return type for the useSchedules hook
 */
export interface UseSchedulesReturn {
  schedules: Schedule[];
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  fetchMore: () => Promise<void>;
  createSchedule: (data: ScheduleFormData) => Promise<Schedule>;
  updateSchedule: (scheduleId: string, data: ScheduleFormData) => Promise<Schedule>;
  deleteSchedule: (scheduleId: string) => Promise<void>;
  updateScheduleStatus: (scheduleId: string, enabled: boolean) => Promise<void>;
  refetch: () => Promise<void>;
}

/**
 * Mock schedules for local development - Updated to match backend schema
 */
const mockSchedules: Schedule[] = [
  {
    scheduleId: 'schedule-1',
    jobId: 'job-1',
    scheduleName: 'Daily Morning Run',
    description: 'Runs every day at 8 AM UTC',
    scheduleExpression: 'cron(0 8 * * ? *)',
    frequencyType: 'CRON',
    timezone: 'UTC',
    status: 'ENABLED',
    nextExecutionTime: new Date(Date.now() + 86400000).toISOString(),
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-15T10:30:00Z',
    createdBy: 'testuser',
    consecutiveFailures: 0,
  },
  {
    scheduleId: 'schedule-2',
    jobId: 'job-1',
    scheduleName: 'Weekly Report',
    description: 'Runs every Monday at 9 AM UTC',
    scheduleExpression: 'cron(0 9 ? * MON *)',
    frequencyType: 'CRON',
    timezone: 'UTC',
    status: 'DISABLED',
    nextExecutionTime: new Date(Date.now() + 604800000).toISOString(),
    createdAt: '2024-01-05T00:00:00Z',
    updatedAt: '2024-01-10T14:20:00Z',
    createdBy: 'testuser',
    consecutiveFailures: 1,
    lastFailureReason: 'Connection timeout to external service',
    lastFailureAt: '2024-01-10T14:20:00Z',
  },
  {
    scheduleId: 'schedule-3',
    jobId: 'job-1',
    scheduleName: 'Monthly Cleanup',
    description: 'Runs on the 1st of every month at midnight',
    scheduleExpression: 'cron(0 0 1 * ? *)',
    frequencyType: 'CRON',
    timezone: 'UTC',
    status: 'AUTO_DISABLED',
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
    createdBy: 'testuser',
    consecutiveFailures: 3,
    lastFailureReason: 'Step Functions execution failed: MaxAttemptsExceeded',
    lastFailureAt: '2024-01-15T12:00:00Z',
    autoDisabledAt: '2024-01-15T12:00:00Z',
    autoDisableReason: 'Exceeded 3 consecutive failures',
  },
];

/**
 * Check if mock data is enabled from settings
 */
const isMockDataEnabled = (): boolean => {
  const settings = (window as any).__ADDAPT_SETTINGS__;
  return settings?.features?.enableMockData === true;
};

/**
 * Custom hook for managing schedule CRUD operations
 *
 * Provides functionality to:
 * - Fetch schedules with pagination
 * - Create new schedules
 * - Update existing schedules
 * - Delete schedules
 * - Toggle schedule enabled/disabled state
 *
 * Uses React Query patterns for caching and state management.
 *
 * @param options - Configuration options including jobId and enabled flag
 * @returns Object containing schedules data and CRUD operations
 *
 * @example
 * ```tsx
 * const {
 *   schedules,
 *   loading,
 *   error,
 *   createSchedule,
 *   updateSchedule,
 *   deleteSchedule,
 *   updateScheduleStatus,
 *   refetch
 * } = useSchedules({ jobId: 'job-123' });
 * ```
 *
 * Requirements: 1.1, 2.6, 3.4, 4.3, 5.2
 */
export const useSchedules = (options: UseSchedulesOptions): UseSchedulesReturn => {
  const { jobId, enabled = true } = options;

  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nextToken, setNextToken] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);

  // Refs for guard logic to avoid stale closures in fetchSchedules callback
  const loadingRef = useRef(false);
  const nextTokenRef = useRef<string | null>(null);

  /**
   * Fetch schedules from API or mock data
   * @param reset - If true, resets pagination and fetches from beginning
   * @param reconcile - If true, requests EventBridge reconciliation (slower but accurate)
   */
  const fetchSchedules = useCallback(
    async (reset: boolean = false, reconcile: boolean = false) => {
      if (loadingRef.current || !jobId) return;

      loadingRef.current = true;
      setLoading(true);
      setError(null);

      try {
        // Use mock data if enabled (standard Amazon practice for local development)
        if (isMockDataEnabled()) {
          // Simulate network delay
          await new Promise((resolve) => setTimeout(resolve, 300));

          // Filter mock schedules by jobId
          const filteredSchedules = mockSchedules.filter((s) => s.jobId === jobId);

          if (reset) {
            setSchedules(filteredSchedules);
          } else {
            setSchedules((prev) => [...prev, ...filteredSchedules]);
          }
          setNextToken(null);
          nextTokenRef.current = null;
          setHasMore(false);
          return;
        }

        const request: ListSchedulesRequest = {
          jobId,
          limit: 10, // Page size as per requirements (10 schedules per page)
          reconcile, // Only reconcile with EventBridge when explicitly requested
        };

        // Add pagination token if not resetting
        if (!reset && nextTokenRef.current) {
          request.nextToken = nextTokenRef.current;
        }

        const response: ListSchedulesResponse = await makeApiCall(
          API_PATHS.SCHEDULES.LIST,
          'POST',
          request
        );

        // Transform backend response to UI format
        const transformedSchedules = transformSchedules(response.schedules || []);

        if (reset) {
          setSchedules(transformedSchedules);
        } else {
          setSchedules((prev) => [...prev, ...transformedSchedules]);
        }

        // Update pagination state
        const newNextToken = response.nextToken || null;
        setNextToken(newNextToken);
        nextTokenRef.current = newNextToken;
        setHasMore(!!response.nextToken);
      } catch (err: any) {
        console.error('Failed to fetch schedules:', err);
        setError(err.message || 'Failed to load schedules');

        // If it's a 404 or the API doesn't exist yet, return empty list
        if (err.message?.includes('404') || err.message?.includes('Not Found')) {
          setSchedules([]);
          setHasMore(false);
          setError(null); // Don't show error for empty state
        }
      } finally {
        loadingRef.current = false;
        setLoading(false);
      }
    },
    [jobId]
  );

  /**
   * Fetch more schedules (pagination)
   */
  const fetchMore = useCallback(async () => {
    if (!hasMore || loading) return;
    await fetchSchedules(false);
  }, [hasMore, loading, fetchSchedules]);

  /**
   * Refetch schedules from the beginning with EventBridge reconciliation
   */
  const refetch = useCallback(async () => {
    setNextToken(null);
    nextTokenRef.current = null;
    await fetchSchedules(true, true);
  }, [fetchSchedules]);

  /**
   * Create a new schedule
   * @param data - Schedule form data
   * @returns The created schedule
   */
  const createSchedule = useCallback(
    async (data: ScheduleFormData): Promise<Schedule> => {
      setError(null);

      try {
        // Use mock data if enabled
        if (isMockDataEnabled()) {
          await new Promise((resolve) => setTimeout(resolve, 300));

          const newSchedule: Schedule = {
            scheduleId: `schedule-${Date.now()}`,
            jobId,
            scheduleName: data.scheduleName,
            description: data.description,
            scheduleExpression: data.scheduleExpression,
            frequencyType: data.frequencyType,
            timezone: data.timezone || 'UTC',
            status: 'ENABLED',
            nextExecutionTime: new Date(Date.now() + 86400000).toISOString(),
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            createdBy: 'mockuser',
          };

          setSchedules((prev) => [newSchedule, ...prev]);
          return newSchedule;
        }

        const request: CreateScheduleRequest = {
          jobId,
          scheduleName: data.scheduleName,
          description: data.description,
          scheduleExpression: data.scheduleExpression,
          frequencyType: data.frequencyType,
          timezone: data.timezone,
          enabled: true,
        };

        const response = await makeApiCall(API_PATHS.SCHEDULES.CREATE, 'POST', request);

        // The API may return the schedule nested under 'schedule' key
        const backendSchedule = response.schedule || response;
        const createdSchedule = transformSchedule(backendSchedule);

        // Add the new schedule to the list
        setSchedules((prev) => [createdSchedule, ...prev]);

        return createdSchedule;
      } catch (err: any) {
        console.error('Failed to create schedule:', err);
        const errorMessage = err.message || 'Failed to create schedule';
        setError(errorMessage);
        throw new Error(errorMessage);
      }
    },
    [jobId]
  );

  /**
   * Update an existing schedule
   * @param scheduleId - ID of the schedule to update
   * @param data - Updated schedule form data
   * @returns The updated schedule
   */
  const updateSchedule = useCallback(
    async (scheduleId: string, data: ScheduleFormData): Promise<Schedule> => {
      setError(null);

      try {
        // Use mock data if enabled
        if (isMockDataEnabled()) {
          await new Promise((resolve) => setTimeout(resolve, 300));

          // Find existing schedule to preserve fields not being updated
          const existingSchedule = schedules.find((s) => s.scheduleId === scheduleId);

          const updatedSchedule: Schedule = {
            scheduleId,
            jobId,
            scheduleName: data.scheduleName,
            description: data.description,
            scheduleExpression: data.scheduleExpression,
            frequencyType: data.frequencyType,
            timezone: data.timezone || 'UTC',
            status: existingSchedule?.status || 'ENABLED',
            nextExecutionTime: new Date(Date.now() + 86400000).toISOString(),
            createdAt: existingSchedule?.createdAt || new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            createdBy: existingSchedule?.createdBy || 'mockuser',
          };

          setSchedules((prev) =>
            prev.map((s) => (s.scheduleId === scheduleId ? updatedSchedule : s))
          );
          return updatedSchedule;
        }

        const request: UpdateScheduleRequest = {
          jobId,
          scheduleId,
          scheduleName: data.scheduleName,
          description: data.description,
          scheduleExpression: data.scheduleExpression,
          frequencyType: data.frequencyType,
          timezone: data.timezone,
        };

        const response = await makeApiCall(API_PATHS.SCHEDULES.UPDATE, 'POST', request);

        // The API may return the schedule nested under 'schedule' key
        // Merge form data to ensure scheduleExpression is available for nextExecutionTime computation
        // (backend update response may not include it)
        const backendSchedule = {
          ...response.schedule || response,
          scheduleExpression: data.scheduleExpression,
          timezone: data.timezone,
        };
        const updatedSchedule = transformSchedule(backendSchedule);

        // Update the schedule in the list
        setSchedules((prev) =>
          prev.map((s) => (s.scheduleId === scheduleId ? updatedSchedule : s))
        );

        return updatedSchedule;
      } catch (err: any) {
        console.error('Failed to update schedule:', err);
        const errorMessage = err.message || 'Failed to update schedule';
        setError(errorMessage);
        throw new Error(errorMessage);
      }
    },
    [jobId, schedules]
  );

  /**
   * Delete a schedule
   * @param scheduleId - ID of the schedule to delete
   */
  const deleteSchedule = useCallback(async (scheduleId: string): Promise<void> => {
    setError(null);

    try {
      // Use mock data if enabled
      if (isMockDataEnabled()) {
        await new Promise((resolve) => setTimeout(resolve, 300));

        setSchedules((prev) => prev.filter((s) => s.scheduleId !== scheduleId));
        return;
      }

      // Backend requires both jobId and scheduleId per Smithy DeleteScheduleRequest
      await makeApiCall(API_PATHS.SCHEDULES.DELETE, 'POST', { jobId, scheduleId });

      // Remove the schedule from the list
      setSchedules((prev) => prev.filter((s) => s.scheduleId !== scheduleId));
    } catch (err: any) {
      console.error('Failed to delete schedule:', err);
      const errorMessage = err.message || 'Failed to delete schedule';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, [jobId]);

  /**
   * Update schedule enabled/disabled status
   * Implements optimistic updates with rollback on failure
   * @param scheduleId - ID of the schedule to update
   * @param enabled - New enabled state
   */
  const updateScheduleStatus = useCallback(
    async (scheduleId: string, enabled: boolean): Promise<void> => {
      setError(null);

      // Store previous state for rollback
      const previousSchedules = [...schedules];

      // Find the schedule to get its jobId
      const schedule = schedules.find((s) => s.scheduleId === scheduleId);
      if (!schedule) {
        throw new Error('Schedule not found');
      }

      // Optimistic update - immediately update UI
      const newStatus: ScheduleStatus = enabled ? 'ENABLED' : 'DISABLED';
      setSchedules((prev) =>
        prev.map((s) =>
          s.scheduleId === scheduleId
            ? { ...s, status: newStatus, updatedAt: new Date().toISOString() }
            : s
        )
      );

      try {
        // Use mock data if enabled
        if (isMockDataEnabled()) {
          await new Promise((resolve) => setTimeout(resolve, 300));
          return;
        }

        const request: UpdateScheduleStatusRequest = {
          jobId: schedule.jobId,
          scheduleId,
          enabled,
        };

        await makeApiCall(API_PATHS.SCHEDULES.UPDATE_STATUS, 'POST', request);
      } catch (err: any) {
        console.error('Failed to update schedule status:', err);

        // Rollback to previous state on failure
        setSchedules(previousSchedules);

        const errorMessage = err.message || 'Failed to update schedule status';
        setError(errorMessage);
        throw new Error(errorMessage);
      }
    },
    [schedules]
  );

  // Auto-fetch on mount if enabled
  useEffect(() => {
    if (enabled && jobId) {
      fetchSchedules(true);
    }
  }, [jobId, enabled]); // Re-fetch when jobId changes

  return {
    schedules,
    loading,
    error,
    hasMore,
    fetchMore,
    createSchedule,
    updateSchedule,
    deleteSchedule,
    updateScheduleStatus,
    refetch,
  };
};

export default useSchedules;
